import { useId } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import { gr } from "../tokens";
import icons from "../icons";
import GrIcon from "./GrIcon";
import GrIconButton from "./GrIconButton";

// Side drawer for forms and filters: opens on the right, 400px (full width on phones).
// Header: 18/500 brand-strong title + labelled ghost close, hairline under it. The body scrolls
// inside; an optional footer (e.g. Cancel + Save) stays at the bottom. Backdrop = overlay token.
export default function GrDrawer({
  open,
  onClose,
  title,
  children,
  footer,
  flush = false,
  className,
  sx,
}) {
  const titleId = `gr-drawer-${useId()}`;
  return (
    <Drawer
      anchor="right"
      open={!!open}
      onClose={onClose}
      className={className}
      // Modal layer: above the app bar and the floating chat box.
      sx={{ zIndex: (theme) => theme.zIndex.modal }}
      BackdropProps={{ sx: { background: gr.color.overlay } }}
      PaperProps={{
        role: "dialog",
        "aria-modal": true,
        "aria-labelledby": titleId,
        sx: {
          width: gr.layout.drawer,
          maxWidth: "100vw",
          border: "none",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          background: gr.color.surface,
          boxShadow: gr.shadow.raised,
          fontFamily: gr.font.family,
          ...sx,
        },
      }}
    >
      <Box
        sx={{
          flex: "none",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: gr.space[3],
          px: gr.space[4],
          py: gr.space[3],
          borderBottom: `1px solid ${gr.color.line}`,
        }}
      >
        <Box
          component="h2"
          id={titleId}
          sx={{
            m: 0,
            minWidth: 0,
            fontFamily: gr.font.family,
            fontSize: gr.font.size.xl,
            fontWeight: gr.font.weight.medium,
            lineHeight: gr.font.lineHeight.tight,
            color: gr.color.brandStrong,
          }}
        >
          {title}
        </Box>
        <GrIconButton variant="ghost" aria-label="Close" onClick={onClose}>
          <GrIcon icon={icons.close} />
        </GrIconButton>
      </Box>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overscrollBehavior: "contain",
          p: flush ? 0 : gr.space[4],
        }}
      >
        {children}
      </Box>
      {footer ? (
        <Box
          sx={{
            flex: "none",
            display: "flex",
            justifyContent: "flex-end",
            gap: gr.space[3],
            px: gr.space[4],
            py: gr.space[3],
            borderTop: `1px solid ${gr.color.line}`,
          }}
        >
          {footer}
        </Box>
      ) : null}
    </Drawer>
  );
}

GrDrawer.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  title: PropTypes.node.isRequired,
  children: PropTypes.node,
  // Buttons that stay at the bottom (quiet Cancel + one primary).
  footer: PropTypes.node,
  // No body padding (content brings its own, e.g. older forms).
  flush: PropTypes.bool,
  className: PropTypes.string,
  sx: PropTypes.object,
};
